import { Autocomplete, FormControl } from '@mui/joy';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { ApplicationAccess } from '~/common/enums';

import { useAutocompleteEnum } from '@/shared/hooks/autocomplete';

import FormikTextField from '@/shared/components/2.0/formik/FormikTextField';
import SwitchField from '@/shared/components/2.0/input/SwitchField';

export type EditSubscriptionFormValues = {
  allowAccessKeyManagement: boolean;
  allowAiUsage: boolean;
  allowCaseManagement: boolean;
  allowSyndicManagement: boolean;
  maxUsers: number;
  maxMailboxes: number;
  maxQrTokens: number;
  applicationAccess: ApplicationAccess[];
};

const EditSubscriptionFormBody = () => {
  const { t } = useTranslation('common');
  const { values, setFieldValue } = useFormikContext<EditSubscriptionFormValues>();

  const access = useAutocompleteEnum({
    callback: (options) => {
      setFieldValue(
        'applicationAccess',
        options.map((option) => option.value),
      );
    },
    enforceSingleOptionPrefill: false,
    multiple: true,
    selected: values.applicationAccess,
    srcEnum: ApplicationAccess,
    translationKey: 'applicationAccess',
  });

  return (
    <div className="flex flex-col gap-2">
      <FormControl className="mb-2 ml-1">
        <SwitchField
          checked={values.allowCaseManagement}
          label={
            values.allowCaseManagement ? t('caseManagementEnabled') : t('caseManagementDisabled')
          }
          labelContainerProps={{ level: 'title-sm' }}
          onChange={() => setFieldValue('allowCaseManagement', !values.allowCaseManagement)}
        />
      </FormControl>
      <FormControl className="mb-2 ml-1">
        <SwitchField
          checked={values.allowSyndicManagement}
          label={
            values.allowSyndicManagement
              ? t('syndicManagementEnabled')
              : t('syndicManagementDisabled')
          }
          labelContainerProps={{ level: 'title-sm' }}
          onChange={() => setFieldValue('allowSyndicManagement', !values.allowSyndicManagement)}
        />
      </FormControl>

      <FormControl className="mb-2 ml-1">
        <SwitchField
          checked={values.allowAccessKeyManagement}
          label={
            values.allowAccessKeyManagement
              ? t('accessKeyManagementEnabled')
              : t('accessKeyManagementDisabled')
          }
          labelContainerProps={{ level: 'title-sm' }}
          onChange={() =>
            setFieldValue('allowAccessKeyManagement', !values.allowAccessKeyManagement)
          }
        />
      </FormControl>

      <FormControl className="mb-2 ml-1">
        <SwitchField
          checked={values.allowAiUsage}
          label={values.allowAiUsage ? t('aiUsageEnabled') : t('aiUsageDisabled')}
          labelContainerProps={{ level: 'title-sm' }}
          onChange={() => setFieldValue('allowAiUsage', !values.allowAiUsage)}
        />
      </FormControl>

      <FormControl className="mb-2 flex flex-row gap-2">
        <p className="w-28">{t('maxUsers')}</p>
        <FormikTextField className="w-16" type="number" name="maxUsers" min={0} fullWidth={false} />
      </FormControl>

      <FormControl className="mb-2 flex flex-row gap-2">
        <p className="w-28 ">{t('maxMailboxes')}</p>
        <FormikTextField
          className="w-16"
          min={0}
          type="number"
          name="maxMailboxes"
          fullWidth={false}
        />
      </FormControl>

      <FormControl className="mb-2 flex flex-row items-center gap-2">
        <p className="w-28">{t('maxQrTokens')}</p>
        <FormikTextField
          className="w-16"
          min={0}
          type="number"
          name="maxQrTokens"
          fullWidth={false}
        />
      </FormControl>

      <FormControl className="flex flex-row items-center gap-2">
        <p className="w-28">{t('applicationAccess.title')}</p>
        <Autocomplete {...access} />
      </FormControl>
    </div>
  );
};

export default EditSubscriptionFormBody;
